/**
 * "/admin" — THE list of AI models Kola offers (2026-09-04).
 *
 * Every model dropdown in the app renders this list: the script brainstorm
 * picker, the sections panel, both thumbnail pickers, and the per-function
 * defaults below. Adding a model here puts it in all of them, on every
 * client's next board open, with no redeploy — which is the whole point.
 * Before this, the thumbnail panel ranked models from a table baked into
 * client code while the other pickers dumped OpenRouter's ~400-entry catalog.
 *
 * A model is an ID, an optional nickname, and whether it makes TEXT or
 * IMAGES. The kind is the one field a picker cannot guess wrong about: an
 * image model offered for the hooks returns nothing usable, and a text model
 * offered for the thumbnail render fails every frame — so the two pickers
 * filter on it. It is pre-filled from the provider's catalog when the id is
 * one the catalog knows, and always overridable, because a brand-new listing
 * may declare no modality at all.
 *
 * The id field is FREE TEXT with the catalog as a `<datalist>` of suggestions,
 * never a `<select>`: naming a model the catalog has not caught up with is
 * exactly the job this list exists to do (the same reasoning the per-function
 * fields have always carried — see the server's ai/model-settings.ts).
 */
import {
  AI_MODEL_LABEL_MAX_LENGTH,
  AI_MODEL_LIST_MAX_COUNT,
  isAiModelId,
  type AiModelEntry,
  type AiModelKind,
  type AiModelListing,
} from "@kola/shared";
import { useEffect, useState } from "react";

import {
  ApiError,
  createAiModelEntry,
  deleteAiModelEntry,
  listAiModels,
  updateAiModelEntry,
} from "../../core/api.js";
import { Modal } from "../../ui/Modal.js";
import { showToast } from "../../ui/toast.js";
import { catalogKind, modelOptionLabel } from "../ai-models/catalog.js";
import { loadAiModels } from "../ai-models/load.js";
import { useAiModels } from "../ai-models/store.js";

/** One datalist for the whole section — every id field points at it. */
const CATALOG_DATALIST_ID = "admin-ai-model-catalog";

/** The user-facing reason for a refused write (the route's error codes). */
function modelErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "duplicate-model":
        return "That model is already on the list.";
      case "invalid-model-id":
        return "A model id is one word — no spaces — of up to 200 characters.";
      case "invalid-label":
        return `A nickname is up to ${AI_MODEL_LABEL_MAX_LENGTH} characters.`;
      case "invalid-kind":
        return "Pick whether the model makes text or images.";
      case "too-many-models":
        return `The list holds up to ${AI_MODEL_LIST_MAX_COUNT} models — remove one to add another.`;
      case "forbidden":
        return "Only an admin can change the model list.";
      case "unknown-model":
        return "That model was already removed.";
      default:
        return `The change failed (${error.status}). Try again.`;
    }
  }
  return "The change failed — check your connection and try again.";
}

/** A draft of one entry's editable fields. */
interface ModelDraft {
  modelId: string;
  label: string;
  kind: AiModelKind;
}

function draftOf(entry: AiModelEntry): ModelDraft {
  return { modelId: entry.modelId, label: entry.label, kind: entry.kind };
}

function sameDraft(a: ModelDraft, b: ModelDraft): boolean {
  return a.modelId === b.modelId && a.label === b.label && a.kind === b.kind;
}

function isValid(draft: ModelDraft): boolean {
  return isAiModelId(draft.modelId) && draft.label.trim().length <= AI_MODEL_LABEL_MAX_LENGTH;
}

/** The id / nickname / kind controls, shared by the add form and each row. */
function ModelFields({
  draft,
  onChange,
  catalog,
  idPrefix,
}: {
  draft: ModelDraft;
  onChange: (next: ModelDraft) => void;
  catalog: readonly AiModelListing[];
  idPrefix: string;
}) {
  return (
    <div className="aim-fields">
      <label className="aim-field aim-field-id">
        <span>Model id</span>
        <input
          className="text-input"
          value={draft.modelId}
          placeholder="e.g. google/gemini-3-pro-image"
          maxLength={200}
          list={CATALOG_DATALIST_ID}
          spellCheck={false}
          autoCapitalize="off"
          onChange={(event) => {
            // Space is never part of a model id, and pasting a padded one is
            // the most common way to end up with an id the provider 404s on.
            const modelId = event.target.value.replace(/\s/g, "");
            // Pre-fill the kind from the catalog while the admin is typing —
            // only until they touch the control themselves, which a differing
            // kind on a known id tells us they have.
            const known = catalogKind(catalog, modelId);
            onChange({ ...draft, modelId, ...(known === undefined ? {} : { kind: known }) });
          }}
        />
      </label>
      <label className="aim-field">
        <span>Nickname</span>
        <input
          className="text-input"
          value={draft.label}
          placeholder="optional"
          maxLength={AI_MODEL_LABEL_MAX_LENGTH}
          spellCheck={false}
          onChange={(event) => onChange({ ...draft, label: event.target.value })}
        />
      </label>
      <label className="aim-field aim-field-kind">
        <span>Makes</span>
        <select
          id={`${idPrefix}-kind`}
          value={draft.kind}
          onChange={(event) => onChange({ ...draft, kind: event.target.value as AiModelKind })}
        >
          <option value="text">Text</option>
          <option value="image">Images</option>
        </select>
      </label>
    </div>
  );
}

/** One saved entry: fields, Save (when dirty), Remove. */
function ModelRow({ entry, catalog }: { entry: AiModelEntry; catalog: readonly AiModelListing[] }) {
  const [draft, setDraft] = useState<ModelDraft>(() => draftOf(entry));
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const dirty = !sameDraft(draft, draftOf(entry));

  // Re-seed when another admin edits the same row under us.
  useEffect(() => {
    setDraft(draftOf(entry));
  }, [entry]);

  const save = async () => {
    setSaving(true);
    try {
      await updateAiModelEntry(entry.id, {
        modelId: draft.modelId.trim(),
        label: draft.label.trim(),
        kind: draft.kind,
      });
      await loadAiModels();
      showToast(`Updated ${draft.modelId.trim()}`);
    } catch (error) {
      showToast(modelErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setConfirmingDelete(false);
    try {
      await deleteAiModelEntry(entry.id);
      await loadAiModels();
      showToast(`Removed ${entry.modelId}`);
    } catch (error) {
      showToast(modelErrorMessage(error));
    }
  };

  return (
    <li className="aim-row">
      <div className="aim-row-head">
        <code className="aim-row-id">{entry.modelId}</code>
        <span className={`aim-kind-tag aim-kind-${entry.kind}`}>
          {entry.kind === "image" ? "Images" : "Text"}
        </span>
        {entry.label.trim() !== "" && <span className="aim-row-label">{entry.label}</span>}
        <div className="spacer" />
        {dirty && (
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setDraft(draftOf(entry))}>
              Revert
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!isValid(draft) || saving}
              onClick={() => void save()}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </>
        )}
        <button
          type="button"
          className="btn btn-ghost aim-remove"
          onClick={() => setConfirmingDelete(true)}
        >
          Remove
        </button>
      </div>
      <ModelFields
        draft={draft}
        onChange={setDraft}
        catalog={catalog}
        idPrefix={`aim-${entry.id}`}
      />
      {confirmingDelete && (
        <Modal title={`Remove ${entry.modelId}?`} onClose={() => setConfirmingDelete(false)}>
          <p>
            It disappears from every model dropdown in Kola. Anyone whose remembered pick was
            this model goes back to the default on their next run, and any function still pinned
            to it below keeps running on it until you change that too.
          </p>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void remove()}>
              Remove
            </button>
          </div>
        </Modal>
      )}
    </li>
  );
}

const EMPTY_DRAFT: ModelDraft = { modelId: "", label: "", kind: "text" };

/** The add form — id, nickname, kind, "Add model". */
function AddModelForm({ atCap, catalog }: { atCap: boolean; catalog: readonly AiModelListing[] }) {
  const [draft, setDraft] = useState<ModelDraft>(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  const valid = isValid(draft);

  const add = async () => {
    setAdding(true);
    try {
      await createAiModelEntry({
        modelId: draft.modelId.trim(),
        label: draft.label.trim(),
        kind: draft.kind,
      });
      await loadAiModels();
      showToast(`Added ${draft.modelId.trim()} — it's in every model dropdown now`);
      setDraft(EMPTY_DRAFT);
    } catch (error) {
      showToast(modelErrorMessage(error));
    } finally {
      setAdding(false);
    }
  };

  return (
    <form
      className="aim-add"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && !adding && !atCap) void add();
      }}
    >
      <div className="aim-row-head">
        <code className="aim-row-id aim-row-id-new">
          {draft.modelId.trim() === "" ? "vendor/model-name" : draft.modelId.trim()}
        </code>
        <div className="spacer" />
        <button type="submit" className="btn btn-primary" disabled={!valid || adding || atCap}>
          {adding ? "Adding…" : "Add model"}
        </button>
      </div>
      <ModelFields draft={draft} onChange={setDraft} catalog={catalog} idPrefix="aim-new" />
      {atCap && (
        <p className="tx-hint">
          The list already holds {AI_MODEL_LIST_MAX_COUNT} models — remove one to add another.
        </p>
      )}
    </form>
  );
}

export function AiModelsSection() {
  const models = useAiModels();
  /**
   * The provider's catalog — SUGGESTIONS for the id field and the source of
   * the kind pre-fill, nothing more. A failure is silent: not being able to
   * reach OpenRouter must not stop an admin typing a model id, which is
   * exactly what this section is for.
   */
  const [catalog, setCatalog] = useState<AiModelListing[]>([]);

  useEffect(() => {
    void listAiModels()
      .then((response) => setCatalog(response.models))
      .catch(() => {
        /* no suggestions, no complaint */
      });
  }, []);

  return (
    <section className="admin-section">
      <div className="admin-section-head">
        <h2>AI models</h2>
        <span className="admin-card-hint">
          The models every AI dropdown in Kola offers — the script's Ask AI, Divide into Sections,
          and both thumbnail pickers. Add one and it is there for everyone; remove one and it is
          gone everywhere. Each picker shows only the models that make what it needs.
        </span>
      </div>

      {catalog.length > 0 && (
        <datalist id={CATALOG_DATALIST_ID}>
          {catalog.map((entry) => (
            <option key={entry.id} value={entry.id} label={modelOptionLabel(entry)} />
          ))}
        </datalist>
      )}

      {models.length === 0 && (
        <p className="admin-card-hint aim-empty">
          The list is empty, so every dropdown offers only its function's default. Add the models
          you want people to be able to choose.
        </p>
      )}

      <ul className="aim-list">
        {models.map((entry) => (
          <ModelRow key={entry.id} entry={entry} catalog={catalog} />
        ))}
      </ul>

      <AddModelForm atCap={models.length >= AI_MODEL_LIST_MAX_COUNT} catalog={catalog} />
    </section>
  );
}
